/* eslint-disable max-lines-per-function */
import type { LucideIcon } from "lucide-react";
import { ArrowLeft, LayoutDashboard, Settings, User, Users } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { LumiLogo } from "../../shared/components/branding/LumiLogo";
import { Spotlight } from "../../shared/components/effects/Spotlight";
import { LoadingSpinner } from "../../shared/components/loading/LoadingSpinner";
import { Toaster } from "../../shared/components/toaster";
import { UserProfileDropdown, type UserProfileDropdownHandle } from "../../shared/components/UserProfileDropdown";
import { useTransitionNavigation } from "../../shared/hooks/useTransitionNavigation";
import { pageTransitions } from "../../shared/transitions/pageTransitions";

// Design tokens for consistency
const NAV_ICON_SIZE = 16;
const NAV_ICON_STROKE = 1.25;

interface NavItem {
  readonly to: string;
  readonly icon: LucideIcon;
  readonly label: string;
}

const NAV_ITEMS: readonly NavItem[] = [
  { to: "/user/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/user/account", icon: User, label: "Account" },
  { to: "/user/profiles", icon: Users, label: "Profiles" },
  { to: "/user/settings", icon: Settings, label: "App Settings" },
] as const;

interface NavLinkWithTooltipProps {
  readonly to: string;
  readonly icon: LucideIcon;
  readonly label: string;
  readonly isActive: boolean;
}

function NavLinkWithTooltip({ to, icon: Icon, label, isActive }: NavLinkWithTooltipProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (document.startViewTransition) {
      document.startViewTransition(() => {
        navigate(to);
      });
    } else {
      navigate(to);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`group relative flex items-center justify-center w-10 h-10 rounded-md transition-all ${
        isActive
          ? "text-lumi-accent bg-neutral-800/0"
          : "text-neutral-500 hover:text-lumi-accent hover:bg-neutral-800/40"
      }`}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      aria-label={label}
      aria-current={isActive ? "page" : undefined}
      type="button"
    >
      <Icon size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />
      <div
        className={`absolute top-full mt-4 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-neutral-900 border border-neutral-700 text-lumi-accent text-xs font-medium rounded-md whitespace-nowrap z-50 pointer-events-none shadow-xl transition-all duration-200 ${
          showTooltip ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1 invisible"
        }`}
      >
        {label}
        <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-neutral-900 border-l border-t border-neutral-700 rotate-45" />
      </div>
    </button>
  );
}

interface IconWithTooltipProps {
  readonly icon: LucideIcon;
  readonly label: string;
  readonly onClick?: () => void;
  readonly className?: string;
}

function IconWithTooltip({ icon: Icon, label, onClick, className = "" }: IconWithTooltipProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  const Component = onClick ? "button" : "div";

  return (
    <Component
      onClick={onClick}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      className={`group relative flex items-center justify-center w-10 h-10 rounded-md transition-all text-neutral-400 hover:text-white hover:bg-neutral-800/40 ${className}`}
      type={onClick ? "button" : undefined}
      aria-label={label}
    >
      <Icon size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />
      <div
        className={`absolute top-full mt-4 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-neutral-900 border border-neutral-700 text-lumi-accent text-xs font-medium rounded-md whitespace-nowrap z-50 pointer-events-none shadow-xl transition-all duration-200 ${
          showTooltip ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1 invisible"
        }`}
      >
        {label}
        <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-neutral-900 border-l border-t border-neutral-700 rotate-45" />
      </div>
    </Component>
  );
}

/**
 * User layout with authentication guard
 * Provides navigation between account, profiles, and settings pages
 */
export function UserLayout(): ReactElement {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { transitionTo } = useTransitionNavigation();
  const [isScrolled, setIsScrolled] = useState(false);
  const userProfileDropdownRef = useRef<UserProfileDropdownHandle>(null);

  // Stable navigation handler (prevent event handler orphaning during View Transitions)
  const handleNavigateHome = useCallback(() => {
    transitionTo("/home", pageTransitions.fullScreenFade);
  }, [transitionTo]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      console.warn("⚠️ User profile access denied - not authenticated, redirecting to sign-in");
      navigate("/sign-in", { replace: true });
    }
  }, [user, loading, navigate]);

  // Show loading state while checking authentication
  if (loading) {
    return <LoadingSpinner centered />;
  }

  // Don't render content until authenticated
  if (!user) {
    return <></>;
  }

  return (
    <div className="min-h-screen bg-black relative">
      {/* Subtle animated spotlight background */}
      <Spotlight />

      {/* User Profile Header */}
      <header className="fixed top-0 left-0 right-0 z-50 safe-top">
        {/* Draggable titlebar region for Electron (macOS only) - absolute positioned to not interfere with layout */}
        <div
          className="absolute top-0 left-0 right-0 h-16 electron-drag-region"
          style={{ WebkitAppRegion: "drag" } as React.CSSProperties}
        />

        {/* Background gradient */}
        <div
          className="bg-gradient-to-b from-black/90 to-black/60 h-full w-full absolute inset-0 transition-opacity duration-700 ease-out pointer-events-none"
          style={{
            opacity: isScrolled ? 1 : 0,
          }}
        />

        <div className="w-full container-padding mx-auto px-2">
          <div className="flex items-center justify-between h-16 pt-1 relative z-20">
            <div className="flex items-center gap-8">
              {/* Spacer for macOS traffic lights - only visible on Electron */}
              <div className="electron-traffic-light-spacer" />
              <LumiLogo
                onClick={handleNavigateHome}
                className="h-4 sm:h-6 pl-4 cursor-pointer"
                ariaLabel="Go to home"
              />
              <Link to="/user/dashboard" className="flex items-center gap-3">
                <span className="px-2 py-1 bg-transparent border border-lumi-accent text-white hover:bg-lumi-accent/10 hover:text-lumi-accent text-[10px] font-bold rounded tracking-wide">
                  USER SETTINGS
                </span>
              </Link>
              <div className="h-6 w-px bg-neutral-800" />
              <nav className="flex items-center gap-1" aria-label="User settings navigation">
                {NAV_ITEMS.map((item) => (
                  <NavLinkWithTooltip
                    key={item.to}
                    to={item.to}
                    icon={item.icon}
                    label={item.label}
                    isActive={location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)}
                  />
                ))}
              </nav>
            </div>
            <div className="flex items-center gap-4">
              <IconWithTooltip
                className="w-5 h-5 touch-target hover:text-lumi-accent rounded-md transition-all hover:scale-110"
                icon={ArrowLeft}
                label="Back to App"
                onClick={handleNavigateHome}
              />
              {!loading &&
                (user ? (
                  <UserProfileDropdown ref={userProfileDropdownRef} />
                ) : (
                  <Link to="/sign-in" className="text-sm font-medium hover:text-neutral-300 transition-colors">
                    Sign In
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </header>

      {/* Page Content */}
      <div className="container mx-auto px-8 py-8 pt-24 relative z-10">
        <Outlet />
      </div>

      {/* Toast Notifications */}
      <Toaster />
    </div>
  );
}
